import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Rss, LayoutGrid } from "lucide-react";

interface RssItem {
  title: string;
  link: string;
  pubDate: string;
  description?: string;
  author?: string;
}

interface FeedSlot {
  id: string;
  platformLabel: string;
  sourceType: "iframe" | "rss_url";
  embedCode?: string | null;
  feedUrl?: string | null;
  isActive?: boolean;
  displayOrder: number;
  items?: RssItem[];
}

interface SocialWallPublic {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  feeds: FeedSlot[];
}

function truncate(text: string, maxLen: number): string {
  const clean = text.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  return clean.length > maxLen ? clean.slice(0, maxLen) + "…" : clean;
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function IframeSlot({ feed }: { feed: FeedSlot }) {
  if (!feed.embedCode) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        {feed.platformLabel}: embed code not configured.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Badge variant="secondary" data-testid={`badge-platform-${feed.id}`}>
          {feed.platformLabel}
        </Badge>
      </div>
      {/* Responsive 16:9 wrapper — works for most social embeds.
          The embed code is admin-entered, not user-generated. */}
      <div
        className="relative w-full aspect-video overflow-hidden rounded-lg [&>iframe]:absolute [&>iframe]:inset-0 [&>iframe]:w-full [&>iframe]:h-full"
        dangerouslySetInnerHTML={{ __html: feed.embedCode }}
        data-testid={`iframe-slot-${feed.id}`}
      />
    </div>
  );
}

function RssSlot({ feed }: { feed: FeedSlot }) {
  const items = feed.items ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Rss className="h-4 w-4 text-muted-foreground" />
        <Badge variant="secondary" data-testid={`badge-platform-${feed.id}`}>
          {feed.platformLabel}
        </Badge>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid={`text-rss-empty-${feed.id}`}>
          No items available for this feed.
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid={`rss-grid-${feed.id}`}>
          {items.map((item, idx) => (
            <a
              key={idx}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group block rounded-lg border p-4 hover:bg-muted/50 transition-colors"
              data-testid={`rss-item-${feed.id}-${idx}`}
            >
              <p className="font-medium text-sm leading-snug group-hover:underline line-clamp-2">
                {item.title}
              </p>
              {item.description && (
                <p className="mt-1.5 text-xs text-muted-foreground line-clamp-3">
                  {truncate(item.description, 160)}
                </p>
              )}
              <div className="mt-2 flex items-center justify-between gap-2">
                {item.pubDate && (
                  <span className="text-xs text-muted-foreground">{formatDate(item.pubDate)}</span>
                )}
                <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-8 py-8">
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-96" />
      </div>
      {[1, 2].map((i) => (
        <div key={i} className="space-y-3">
          <Skeleton className="h-6 w-24" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((j) => (
              <Skeleton key={j} className="h-32 w-full rounded-lg" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SocialWallPage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: wall, isLoading, isError, error } = useQuery<SocialWallPublic>({
    queryKey: ["/api/public/social-wall", slug],
    queryFn: () =>
      fetch(`/api/public/social-wall/${slug}`, { credentials: "include" }).then(async (r) => {
        if (r.status === 404) throw new Error("not_found");
        if (!r.ok) throw new Error("error");
        return r.json();
      }),
    staleTime: 60_000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4">
        <LoadingSkeleton />
      </div>
    );
  }

  if (isError) {
    const isNotFound = (error as Error)?.message === "not_found";
    return (
      <div className="max-w-5xl mx-auto px-4 py-24 text-center space-y-4" data-testid="state-not-found">
        <LayoutGrid className="h-12 w-12 mx-auto text-muted-foreground" />
        <h1 className="text-2xl font-bold">
          {isNotFound ? "Wall not found" : "Something went wrong"}
        </h1>
        <p className="text-muted-foreground">
          {isNotFound
            ? "This social wall doesn't exist or hasn't been published yet."
            : "We couldn't load this social wall. Please try again later."}
        </p>
      </div>
    );
  }

  const activeFeeds = (wall?.feeds ?? [])
    .filter((f) => f.isActive !== false)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 space-y-10">
      <div>
        <h1 className="text-3xl font-bold" data-testid="heading-wall-name">{wall?.name}</h1>
        {wall?.description && (
          <p className="mt-2 text-muted-foreground" data-testid="text-wall-description">
            {wall.description}
          </p>
        )}
      </div>

      {activeFeeds.length === 0 ? (
        <div className="py-20 text-center space-y-3" data-testid="state-empty">
          <Rss className="h-12 w-12 mx-auto text-muted-foreground" />
          <p className="text-lg font-medium">Nothing here yet</p>
          <p className="text-sm text-muted-foreground">
            This wall doesn't have any active feeds configured yet. Check back soon.
          </p>
        </div>
      ) : (
        <div className="space-y-12">
          {activeFeeds.map((feed) => (
            <div key={feed.id} data-testid={`feed-slot-${feed.id}`}>
              {feed.sourceType === "iframe" ? (
                <IframeSlot feed={feed} />
              ) : (
                <RssSlot feed={feed} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
